import { useCallback, useEffect, useRef, useState } from 'react';

import { DonutGraph, Shimmer } from '@flexo/atoms';
import { useTranslation } from 'react-i18next';
import { CommunityStatsSkeleton } from './community-stats-skeleton';
import styles from './community-stats.module.scss';

import { App as CapApp } from '@capacitor/app';
import { formatToMaxFourDigits } from '@flexo/general';
import {
  useKpiData,
  useThrottledHaptics,
  useThrottledTouch,
} from '@flexo/hooks';
import { useSelector } from 'react-redux';

export interface CommunityStatsData {
  totalAmount: string;
  totalAmounttype: string;
  colors: string[];
  keys: string[];
  title: string;
  percentage: number;
}
export interface CommunityStatsProps {
  from_network: CommunityStatsData;
  to_network: CommunityStatsData;
  shared: CommunityStatsData;
  dataInTimeSpan: boolean;
}
export const communityPlaceholderData = [{ label: '', value: 100 }];

export function CommunityStats(props) {
  const kpi = useSelector((state: any) => state.kpi);
  const metadata = useSelector((state: any) => state.metadata);

  const { CommunityStats__Stat } = styles;
  // FLEXO-6077 #2: web-only modifier for extra donut vertical padding. Gated on
  // VITE_APP_IS_WEB so the shared mobile (kwick) community stays unchanged.
  const isWeb = (import.meta as any).env?.VITE_APP_IS_WEB === 'true';
  const [selected, setSelected] = useState<string | null>(null);
  const activeTouchRef = useRef<string | null>(null);
  const isMountedRef = useRef(true);
  const appStateListenerRef = useRef<any>(null);
  const vibrate = useThrottledHaptics();
  const shouldAllowTouch = useThrottledTouch(350);
  const ThemeStore = useSelector((state: any) => state.theme);
  const { t } = useTranslation();

  const LOADING_STATE = kpi?.ComparisonKpiDataCommunityAsyncStatus;

  const comparisonKpiApiData =
    kpi?.ComparisonCommunity?.['comparison']?.[0]?.kpi || ([] as any);

  // FLEXO-6077 #5: the comparison endpoint returns `community_shared_total` doubled
  // for Kwick whenever the backend country_code lookup does not resolve to 'ch'
  // (it falls into the `total_shared * 2` branch — a physically impossible figure
  // that can exceed imported energy). The raw, community-wide shared energy lives
  // in the EnergyCommunity bucket as `total_shared` and is always the correct value
  // for the Kwick (CH) app. Prefer it as a fallback; in the healthy case the two
  // coincide so nothing changes, and when the comparison value is doubled this
  // corrects the donut. site_shared_contribution stays raw (not doubled), so the
  // per-site rate is recomputed against the corrected total below.
  const energyCommunityKpi =
    kpi?.EnergyCommunity?.['1day']?.[0]?.kpi || ([] as any);
  const rawCommunitySharedValue = energyCommunityKpi?.find(
    (el) => el?.name === 'total_shared'
  )?.value;
  const correctedCommunityShared =
    rawCommunitySharedValue != null && rawCommunitySharedValue !== 0
      ? rawCommunitySharedValue
      : null;

  // Use the new hook for KPI data access
  const { getCardValue: getKpiCardValue } = useKpiData(
    comparisonKpiApiData,
    'energy'
  );

  // All hooks must be called before any conditional returns
  const endHold = useCallback(() => {
    activeTouchRef.current = null;
    if (isMountedRef.current) {
      setSelected(null);
    }
  }, []);

  // Handle app background
  useEffect(() => {
    const handleAppStateChange = ({ isActive }: { isActive: boolean }) => {
      if (!isActive) {
        console.log(
          '[CommunityStats] 📱 App going to background - clearing state'
        );
        activeTouchRef.current = null;
        if (isMountedRef.current) {
          setSelected(null);
        }
      } else {
        console.log('[CommunityStats] 📱 App resumed to foreground');
      }
    };

    CapApp.addListener('appStateChange', handleAppStateChange).then(
      (listener) => {
        appStateListenerRef.current = listener;
      }
    );

    return () => {
      if (appStateListenerRef.current) {
        appStateListenerRef.current.remove();
      }
    };
  }, []);

  // Global touch end listener to handle cases where element re-renders during touch
  useEffect(() => {
    const handleGlobalTouchEnd = () => {
      if (activeTouchRef.current !== null) {
        endHold();
      }
    };

    const handleGlobalTouchCancel = () => {
      if (activeTouchRef.current !== null) {
        endHold();
      }
    };

    // Use capture phase to ensure we catch the event before it's stopped
    document.addEventListener('touchend', handleGlobalTouchEnd, true);
    document.addEventListener('touchcancel', handleGlobalTouchCancel, true);

    return () => {
      console.log('[CommunityStats] 🔄 Component unmounting - cleanup');
      isMountedRef.current = false;

      document.removeEventListener('touchend', handleGlobalTouchEnd, true);
      document.removeEventListener(
        'touchcancel',
        handleGlobalTouchCancel,
        true
      );
    };
  }, [endHold]);

  // Replace the current loading state check with the consistent pattern
  if (LOADING_STATE === 'PENDING' && !comparisonKpiApiData.length) {
    return (
      <CommunityStatsSkeleton
        props={props.data}
        count={props.data?.length || 3}
        gradientBorder={props.gradientBorder}
        dataInTimeSpan={props.dataInTimeSpan}
        isLoading={LOADING_STATE === 'PENDING'}
      />
    );
  }

  const getCardValue = (key) => {
    const cardValue = getKpiCardValue(key);
    // FLEXO-6077 #5: substitute the raw community total_shared for the (possibly
    // doubled) comparison value on the shared donut.
    if (key === 'community_shared_total' && correctedCommunityShared != null) {
      return { value: correctedCommunityShared, unit: cardValue?.unit || 'kWh' };
    }
    if (cardValue?.value === 0 || !cardValue?.value) {
      return {
        value: '-.-',
        unit: '',
      };
    }
    return cardValue;
  };
  const getTotalFormattedValue = (key) => {
    // FLEXO-6077 #5: same correction for the donut's "community" graph segment.
    if (key === 'community_shared_total' && correctedCommunityShared != null) {
      const formatted = getKpiCardValue(key);
      return { ...formatted, value: correctedCommunityShared };
    }
    return getKpiCardValue(key);
  };

  // WORKAROUND: The comparison API returns null for site_*_contribution absolute
  // values when the request has siteId: [] (admin/supervisor role), but correctly
  // returns 100% for the corresponding rate. When the rate is 100% and the absolute
  // value is null, we use the community total as the site contribution so the donut
  // renders fully colored instead of appearing empty/faded.
  // This should be fixed in the backend (comparison endpoint should return absolute
  // values for all sites when siteId is empty for admin/supervisor).
  const formatGraphData = (item, percentageRate?: number) => {
    const result = item.map((el, index) => {
      const resolved = getTotalFormattedValue(el?.value)?.value;

      // If the site contribution value is null/missing and the rate is 100%,
      // use the other graphData entry's value (the community total) as fallback.
      let value = resolved;
      if ((value == null || value === '-.-') && percentageRate === 100 && item.length === 2) {
        const otherIndex = index === 0 ? 1 : 0;
        value = getTotalFormattedValue(item[otherIndex]?.value)?.value;
      }

      return {
        id: el?.id,
        label: el?.label,
        value,
        formattedValue: getTotalFormattedValue(el?.formattedValue)?.value,
      };
    });
    return result;
  };

  const getColors = (item) => {
    const color = item.map((el) => {
      return ThemeStore.colors[el.color] + el.colorShade;
    });
    return color;
  };

  const getPercentageValue = (item) => {
    // FLEXO-6077 #5: the backend site_shared_contribution_rate is derived from the
    // doubled community_shared_total, so it reads half the real share. When we have
    // corrected the community total, recompute the rate from the raw site
    // contribution against it (in the healthy case this equals the backend value).
    if (
      item.percentage === 'site_shared_contribution_rate' &&
      correctedCommunityShared != null
    ) {
      const siteShared = comparisonKpiApiData?.find(
        (el) => el.name === 'site_shared_contribution'
      )?.value;
      if (siteShared != null && correctedCommunityShared) {
        return Math.round((siteShared / correctedCommunityShared) * 100);
      }
    }
    const percentage = comparisonKpiApiData?.find(
      (el) => el.name === item.percentage
    )?.value;
    if (percentage === 0 || !percentage) {
      return '-.-';
    }
    return percentage;
  };

  const startHold = (valueLabel) => {
    if (selected !== null || allValuesZeroOrNull) return;
    if (!shouldAllowTouch()) {
      return; // Throttled (expected)
    }

    // Check if community value is 0 in the formatted graph data
    const communityItem = formatGraphData(valueLabel.graphData, getPercentageValue(valueLabel)).find(
      (item) => item.label === 'community'
    );
    if (communityItem && communityItem.value === 0) return;

    activeTouchRef.current = valueLabel.title;
    if (isMountedRef.current) {
      setSelected(valueLabel.title);
    }
    vibrate();
  };

  // Check if all values are 0 or null to disable interactions
  const allValuesZeroOrNull =
    props.data?.every((item) => {
      const percentageValue = getPercentageValue(item);
      const formattedGraphItems = formatGraphData(item.graphData, percentageValue);
      const totalAmountValue = getCardValue(item.totalAmount)?.value;

      // Check if total amount is zero/null/placeholder
      const isTotalZeroOrNull =
        !totalAmountValue ||
        totalAmountValue === 0 ||
        totalAmountValue === '-.-';

      // Check if percentage is zero/null/placeholder
      const isPercentageZeroOrNull =
        !percentageValue || percentageValue === 0 || percentageValue === '-.-';

      // Check if all graph data values are zero/null
      const areAllGraphValuesZero = formattedGraphItems?.every(
        (graphItem) =>
          !graphItem.value || graphItem.value === 0 || graphItem.value === '-.-'
      );

      return (
        isTotalZeroOrNull && isPercentageZeroOrNull && areAllGraphValuesZero
      );
    }) ?? true;

  const sites = metadata?.entities?.sites ?? [];

  const hasProsumer = Array.isArray(sites)
    ? sites.some((site: any) => {
        if (site?.communityId === metadata?.selectedEntity?.communityId) {
          const match = Array.isArray(site?.siteType)
            ? site.siteType.some((type: any) => {
                const isActiveProsumer =
                  type?.status === 'active' && type?.siteType === 'prosumer';

                return isActiveProsumer;
              })
            : false;
          return match;
        }
        return false;
      })
    : false;

  return (
    <div
      className={`${styles.CommunityStats} ${
        isWeb ? styles.CommunityStats__Web : ''
      } ${props.gradientBorder && styles.CommunityStats__GradientBorder}`}
      style={{ position: 'relative' }}
    >
      {LOADING_STATE === 'PENDING' && <Shimmer />}
      {props.data.map((item, index) => {
        const formattedGraphItems = formatGraphData(item.graphData, getPercentageValue(item));
        const donutColors = getColors(item.graphData);
        const donutKeys = formattedGraphItems.map((d) => d.label);
        return (
          <div
            className={CommunityStats__Stat}
            key={item.title}
            style={{
              cursor: allValuesZeroOrNull ? 'default' : 'pointer',
            }}
            {...(!allValuesZeroOrNull && {
              onTouchStart: (e) => {
                e.preventDefault();
                startHold(item);
              },
              onTouchEnd: () => {
                endHold();
              },
              onTouchCancel: () => {
                endHold();
              },
              // FLEXO-6077: desktop hover mirrors the mobile tap-and-hold — show the
              // donut detail on mouse enter, hide it on mouse leave.
              onMouseEnter: () => startHold(item),
              onMouseLeave: () => endHold(),
            })}
          >
            <DonutGraph
              width={105}
              height={105}
              graphData={formattedGraphItems}
              colors={donutColors}
              keys={donutKeys}
              totalAmount={formatToMaxFourDigits(
                getCardValue(item.totalAmount)?.value
              )}
              totalAmounttype={getCardValue(item.totalAmount)?.unit}
              selectedId={
                allValuesZeroOrNull
                  ? null
                  : selected === item.title
                  ? item.graphData[0]?.id
                  : null
              }
              setSelectedId={
                allValuesZeroOrNull
                  ? () => null
                  : (id) => setSelected(id ? item.title : null)
              }
              animate={false}
              dataInTimeSpan={true}
              isCommunityStats={true}
            />
            <div
              className={styles.CommunityStats__Stat__Information}
              style={{
                cursor: allValuesZeroOrNull ? 'default' : 'pointer',
              }}
            >
              <div className={`heading5 ${styles.CommunityStats__Stat__Title}`}>
                {t(`widgets.communityStats.${item.title}`)}
              </div>
              {item.title === 'production' && !hasProsumer ? null : (
                <div
                  className={`detail ${styles.CommunityStats__Stat__Description}`}
                >
                  {t(`widgets.communityStats.me`, {
                    percentage: getPercentageValue(item),
                  })}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default CommunityStats;
